import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingDown, Globe, DollarSign, Brain, Loader2, AlertTriangle, CheckCircle2, XCircle, Save, History, Trash2, Clock, FileDown, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import jsPDF from 'jspdf';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';

interface ETFEntry {
  ticker: string;
  name: string;
  leverage: string;
  region: 'US' | 'Europe';
  category: 'Non-Leveraged' | 'Leveraged';
  underlying: string;
  pros: string[];
  cons: string[];
}

const US_ETFS: ETFEntry[] = [
  // ── Non-Leveraged US ──
  { ticker: 'SH', name: 'ProShares Short S&P500', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'S&P 500', pros: ['Simple inverse exposure', 'No leverage decay', 'High liquidity, AUM >$1B'], cons: ['Capped upside on crashes', 'Daily rebalancing drag', 'Expense ratio ~0.89%'] },
  { ticker: 'PSQ', name: 'ProShares Short QQQ', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'Nasdaq 100', pros: ['Direct Nasdaq hedge', 'No leverage amplification', 'Good for tech-heavy portfolios'], cons: ['Sector concentrated', 'Tracking error over time', 'Expense ratio ~0.95%'] },
  { ticker: 'DOG', name: 'ProShares Short Dow30', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'Dow Jones 30', pros: ['Blue-chip hedge', 'Lower volatility than QQQ short', 'Stable inverse exposure'], cons: ['Only 30 stocks', 'Less diversified', 'Moderate liquidity'] },
  { ticker: 'RWM', name: 'ProShares Short Russell 2000', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'Russell 2000', pros: ['Small-cap hedge', 'Broad market coverage', 'Good recession hedge'], cons: ['Higher spread costs', 'More volatile underlying', 'Lower AUM'] },
  { ticker: 'SBB', name: 'ProShares Short SmallCap600', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'S&P SmallCap 600', pros: ['Small-cap quality filter', 'S&P selection criteria', 'Recession hedge'], cons: ['Very low volume', 'Wide bid-ask spread', 'Limited AUM'] },
  { ticker: 'EFZ', name: 'ProShares Short MSCI EAFE', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'MSCI EAFE', pros: ['International hedge via US exchange', 'Developed markets exposure', 'Currency diversification'], cons: ['Indirect Europe exposure', 'Includes Japan/Australia', 'Lower volume'] },
  { ticker: 'EUM', name: 'ProShares Short MSCI Emerging Markets', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'MSCI Emerging Markets', pros: ['EM bearish hedge', 'Covers China, India, Brazil', 'Geopolitical risk hedge'], cons: ['Broad EM basket dilutes conviction', 'Currency basket risk', 'Lower AUM'] },
  { ticker: 'REK', name: 'ProShares Short Real Estate', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'Dow Jones US Real Estate', pros: ['Real estate sector hedge', 'Rate-sensitive short', 'Good in rising rate environment'], cons: ['Narrow sector', 'REIT-specific risks', 'Lower volume'] },
  { ticker: 'SEF', name: 'ProShares Short Financials', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'Dow Jones US Financials', pros: ['Financial sector hedge', 'Banking crisis protection', 'No leverage decay'], cons: ['Narrow sector', 'Financials can be resilient', 'Low AUM'] },
  { ticker: 'SJB', name: 'ProShares Short High Yield', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'Markit iBoxx USD High Yield', pros: ['Credit spread hedge', 'Profits from credit stress', 'Complementary to equity shorts'], cons: ['Bond market complexity', 'Lower correlation to equities', 'Expense ratio ~0.95%'] },
  { ticker: 'TBF', name: 'ProShares Short 20+ Year Treasury', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: '20+ Year US Treasury', pros: ['Long-duration bond short', 'Rising rate play', 'Portfolio diversifier'], cons: ['Inverse to safe haven', 'Timing dependent', 'Not an equity hedge'] },
  { ticker: 'YXI', name: 'ProShares Short FTSE China 50', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'FTSE China 50', pros: ['China bearish play, no leverage', 'Geopolitical hedge', 'Simple structure'], cons: ['Government intervention risk', 'Currency complexity', 'Low AUM'] },
  { ticker: 'MYY', name: 'ProShares Short MidCap400', leverage: '-1x', region: 'US', category: 'Non-Leveraged', underlying: 'S&P MidCap 400', pros: ['Mid-cap hedge', 'Broad market segment', 'No leverage risk'], cons: ['Low volume', 'Less popular segment', 'Wide spreads'] },
  // ── Leveraged US ──
  { ticker: 'SDS', name: 'ProShares UltraShort S&P500', leverage: '-2x', region: 'US', category: 'Leveraged', underlying: 'S&P 500', pros: ['2x amplified returns on down days', 'High liquidity', 'Effective short-term hedge'], cons: ['Severe decay in sideways markets', 'Not for holding >1 day', 'Expense ratio ~0.90%'] },
  { ticker: 'SPXU', name: 'ProShares UltraPro Short S&P500', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'S&P 500', pros: ['Maximum leverage on S&P decline', 'Very high liquidity', 'Strong crash protection'], cons: ['Extreme volatility decay', 'Can lose 90%+ in bull runs', 'Day-trading instrument only'] },
  { ticker: 'QID', name: 'ProShares UltraShort QQQ', leverage: '-2x', region: 'US', category: 'Leveraged', underlying: 'Nasdaq 100', pros: ['2x Nasdaq short', 'Tech sector hedge', 'Good for earnings protection'], cons: ['High decay in volatile Nasdaq', 'Not for multi-day holds', 'Concentrated risk'] },
  { ticker: 'SQQQ', name: 'ProShares UltraPro Short QQQ', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'Nasdaq 100', pros: ['Maximum Nasdaq bearish bet', 'Extremely liquid', 'Massive crash payoff'], cons: ['Destroys capital in bull markets', 'Daily reset compounding', 'Only for intraday/very short term'] },
  { ticker: 'SDOW', name: 'ProShares UltraPro Short Dow30', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'Dow Jones 30', pros: ['3x Dow short', 'Blue-chip crash hedge', 'Lower vol than SQQQ'], cons: ['Same decay problems', 'Narrow index', 'Short-term only'] },
  { ticker: 'DXD', name: 'ProShares UltraShort Dow30', leverage: '-2x', region: 'US', category: 'Leveraged', underlying: 'Dow Jones 30', pros: ['2x Dow short', 'Moderate leverage', 'Blue-chip focus'], cons: ['Daily compounding', '30-stock concentration', 'Not for multi-day'] },
  { ticker: 'TWM', name: 'ProShares UltraShort Russell 2000', leverage: '-2x', region: 'US', category: 'Leveraged', underlying: 'Russell 2000', pros: ['2x small-cap short', 'Good recession play', 'Moderate leverage'], cons: ['Volatile underlying', 'Decay in sideways market', 'Lower AUM than SDS'] },
  { ticker: 'TZA', name: 'Direxion Daily Small Cap Bear 3X', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'Russell 2000', pros: ['3x small-cap short', 'Best recession play', 'Small caps fall hardest'], cons: ['Extreme decay', 'Very volatile', 'Requires precise timing'] },
  { ticker: 'SPXS', name: 'Direxion Daily S&P 500 Bear 3X', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'S&P 500', pros: ['Alternative to SPXU', 'Direxion management', 'Same 3x short S&P'], cons: ['Lower AUM than SPXU', 'Same decay issues', 'Short-term only'] },
  { ticker: 'FAZ', name: 'Direxion Daily Financial Bear 3X', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'Russell 1000 Financials', pros: ['Sector-specific financial short', 'Great in banking crises', 'High beta sector'], cons: ['Narrow sector bet', 'Extreme volatility', 'Can blow up quickly'] },
  { ticker: 'SOXS', name: 'Direxion Daily Semiconductor Bear 3X', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'ICE Semiconductor', pros: ['Short semiconductors 3x', 'Great for AI/chip correction', 'High-conviction trade'], cons: ['Most volatile 3x ETF', 'Semis can rally 50%+ quickly', 'Extreme risk'] },
  { ticker: 'YANG', name: 'Direxion Daily FTSE China Bear 3X', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'FTSE China 50', pros: ['China bearish bet from US market', 'Geopolitical hedge', '3x leverage'], cons: ['Regulatory risk', 'Government intervention', 'Extreme decay'] },
  { ticker: 'ERY', name: 'Direxion Daily Energy Bear 2X', leverage: '-2x', region: 'US', category: 'Leveraged', underlying: 'Energy Select Sector', pros: ['Energy sector short', 'Oil price decline play', 'Sector-focused'], cons: ['Oil geopolitics unpredictable', 'Supply shocks can spike energy', 'Decay risk'] },
  { ticker: 'LABD', name: 'Direxion Daily S&P Biotech Bear 3X', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'S&P Biotechnology Select', pros: ['3x biotech short', 'High-beta sector', 'FDA risk hedge'], cons: ['Biotech can gap up massively', 'Binary event risk', 'Extreme volatility'] },
  { ticker: 'WEBS', name: 'Direxion Daily Dow Jones Internet Bear 3X', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'Dow Jones Internet Composite', pros: ['3x internet/tech short', 'Targets Big Tech', 'High conviction trade'], cons: ['Tech can rally fast', 'Concentrated in mega-caps', 'Extreme decay'] },
  { ticker: 'DRIP', name: 'Direxion Daily S&P Oil & Gas E&P Bear 2X', leverage: '-2x', region: 'US', category: 'Leveraged', underlying: 'S&P Oil & Gas E&P Select', pros: ['Oil producers short', 'Recession/demand drop play', 'Sector-specific'], cons: ['OPEC cuts can reverse trend', 'Geopolitical sensitivity', 'Daily decay'] },
  { ticker: 'TECS', name: 'Direxion Daily Technology Bear 3X', leverage: '-3x', region: 'US', category: 'Leveraged', underlying: 'Technology Select Sector', pros: ['3x technology sector short', 'AI/tech bubble hedge', 'High liquidity'], cons: ['Tech dominance can persist', 'Extreme decay', 'Massive whipsaws'] },
  { ticker: 'SKF', name: 'ProShares UltraShort Financials', leverage: '-2x', region: 'US', category: 'Leveraged', underlying: 'Dow Jones US Financials', pros: ['2x financial short', 'Banking stress play', 'Moderate leverage'], cons: ['Financials can be defensive', 'Daily compounding', 'Narrower than broad market'] },
  { ticker: 'SRS', name: 'ProShares UltraShort Real Estate', leverage: '-2x', region: 'US', category: 'Leveraged', underlying: 'Dow Jones US Real Estate', pros: ['2x real estate short', 'Rate hike beneficiary', 'CRE stress play'], cons: ['REITs can be resilient', 'Dividend yields attract buyers', 'Decay risk'] },
  { ticker: 'UVXY', name: 'ProShares Ultra VIX Short-Term Futures', leverage: '1.5x', region: 'US', category: 'Leveraged', underlying: 'VIX Short-Term Futures', pros: ['Volatility spike play', 'Massive payoff in panics', 'Portfolio insurance'], cons: ['Contango decay destroys value', 'Loses ~60-80%/year in calm markets', 'Only for very short-term'] },
  { ticker: 'VIXY', name: 'ProShares VIX Short-Term Futures', leverage: '1x', region: 'US', category: 'Non-Leveraged', underlying: 'VIX Short-Term Futures', pros: ['Direct volatility exposure', 'No leverage', 'Panic hedge'], cons: ['Contango decay ~40-60%/year', 'Not a long-term hold', 'Complex product'] },
];

const EUROPE_ETFS: ETFEntry[] = [
  // ── Non-Leveraged Europe ──
  { ticker: 'XSPS', name: 'Xtrackers S&P 500 Inverse Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'S&P 500', pros: ['UCITS-compliant', 'EUR-denominated option', 'Daily inverse'], cons: ['Currency risk (USD/EUR)', 'Lower liquidity than US', 'Tracking error'] },
  { ticker: 'XSSX', name: 'Xtrackers Euro Stoxx 50 Short Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'Euro Stoxx 50', pros: ['Direct Eurozone hedge', 'No currency risk for EUR investors', 'UCITS'], cons: ['Limited to 50 stocks', 'Daily rebalancing', 'Lower AUM'] },
  { ticker: 'XSDX', name: 'Xtrackers ShortDAX Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'DAX 40', pros: ['German market hedge', 'EUR denominated', 'Well-known index'], cons: ['Country-specific risk', 'Daily reset', 'Limited to Germany'] },
  { ticker: 'SUK2', name: 'Lyxor FTSE 100 Daily Short', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'FTSE 100', pros: ['UK market hedge', 'GBP denominated', 'Defensive sector bias'], cons: ['GBP currency risk', 'Brexit-impacted', 'Lower liquidity'] },
  { ticker: 'XMBS', name: 'Xtrackers MSCI World Inverse Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'MSCI World', pros: ['Global diversified short', 'UCITS', 'Broad hedge'], cons: ['Diluted by many markets', 'Currency basket risk', 'Lower conviction'] },
  { ticker: 'XSEM', name: 'Xtrackers MSCI EM Inverse Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'MSCI Emerging Markets', pros: ['EM short from EU exchange', 'UCITS', 'Geopolitical hedge'], cons: ['Broad basket', 'Currency overlay', 'Lower liquidity'] },
  { ticker: 'BX4', name: 'Amundi CAC 40 Daily -1x Inverse', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'CAC 40', pros: ['French market hedge', 'EUR denominated', 'Simple -1x'], cons: ['Country-specific', 'Only 40 stocks', 'Daily tracking'] },
  { ticker: 'XSMI', name: 'Xtrackers SMI Short Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'SMI (Swiss Market Index)', pros: ['Swiss market hedge', 'CHF denominated', 'Defensive blue-chips'], cons: ['Only 20 stocks', 'Nestle/Roche/Novartis heavy', 'Country-specific'] },
  { ticker: 'XNAS', name: 'Xtrackers Nasdaq 100 Short Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'Nasdaq 100', pros: ['Nasdaq short from EU', 'UCITS compliant', 'Tech hedge'], cons: ['USD/EUR currency risk', 'Daily rebalancing', 'Tracking error'] },
  { ticker: 'LYXBNK', name: 'Amundi Euro Stoxx Banks Short Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'Euro Stoxx Banks', pros: ['EU banking sector hedge', 'No leverage', 'Banking crisis protection'], cons: ['Narrow sector', 'Low AUM', 'Spread costs'] },
  { ticker: 'XSEM', name: 'Xtrackers MSCI Japan Inverse Daily', leverage: '-1x', region: 'Europe', category: 'Non-Leveraged', underlying: 'MSCI Japan', pros: ['Japan market hedge from EU', 'UCITS', 'Yen exposure hedge'], cons: ['Yen/EUR currency layer', 'Niche product', 'Low AUM'] },
  // ── Leveraged Europe ──
  { ticker: 'X25S', name: 'Xtrackers S&P 500 2x Inverse Daily', leverage: '-2x', region: 'Europe', category: 'Leveraged', underlying: 'S&P 500', pros: ['2x S&P short from Europe', 'UCITS compliant', 'EUR denominated'], cons: ['Double currency + leverage decay', 'Complex tracking', 'Short-term only'] },
  { ticker: 'BX4L', name: 'Amundi CAC 40 Daily -2x Inverse', leverage: '-2x', region: 'Europe', category: 'Leveraged', underlying: 'CAC 40', pros: ['French market 2x short', 'EUR denominated', 'Good during EU crises'], cons: ['Country risk', 'Leverage decay', 'Lower AUM'] },
  { ticker: 'LYX0FV', name: 'Amundi Euro Stoxx 50 Daily -2x Inverse', leverage: '-2x', region: 'Europe', category: 'Leveraged', underlying: 'Euro Stoxx 50', pros: ['2x Eurozone short', 'UCITS', 'Liquid in EU hours'], cons: ['Daily compounding loss', 'Short-term instrument', 'Spread costs'] },
  { ticker: 'DES2', name: 'Xtrackers ShortDAX x2 Daily', leverage: '-2x', region: 'Europe', category: 'Leveraged', underlying: 'DAX 40', pros: ['2x German market short', 'EUR denominated', 'High German exposure'], cons: ['Country concentration', 'Leverage decay', 'Daily reset'] },
  { ticker: 'XSMU', name: 'Xtrackers SMI Short x2 Daily', leverage: '-2x', region: 'Europe', category: 'Leveraged', underlying: 'SMI (Swiss Market Index)', pros: ['2x Swiss market short', 'CHF denominated', 'Pharma/banking hedge'], cons: ['Country-specific', 'Only 20 stocks', 'Leverage decay'] },
  { ticker: 'UK2S', name: 'Amundi FTSE 100 Daily -2x Inverse', leverage: '-2x', region: 'Europe', category: 'Leveraged', underlying: 'FTSE 100', pros: ['2x UK short', 'GBP denominated', 'EU-listed UCITS'], cons: ['GBP currency risk', 'Daily reset', 'Leverage decay'] },
  { ticker: '3USS', name: 'WisdomTree S&P 500 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'S&P 500', pros: ['3x S&P short from European exchange', 'ETP structure', 'Maximum leverage'], cons: ['Extreme decay', 'Currency overlay', 'Intraday only'] },
  { ticker: '3STS', name: 'WisdomTree Euro Stoxx 50 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'Euro Stoxx 50', pros: ['3x Eurozone short', 'No currency risk for EUR', 'Crisis protection'], cons: ['Extreme volatility', 'Daily compounding', 'Can lose 95%+ quickly'] },
  { ticker: '3DES', name: 'WisdomTree DAX 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'DAX 40', pros: ['3x DAX short', 'Max leverage on Germany', 'EUR denominated'], cons: ['Extreme decay risk', 'Only for day trades', 'Can be delisted'] },
  { ticker: '3UKS', name: 'WisdomTree FTSE 100 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'FTSE 100', pros: ['3x UK short', 'GBP crash hedge', 'High leverage'], cons: ['GBP currency risk', 'Extreme decay', 'Very short-term'] },
  { ticker: '3NQS', name: 'WisdomTree Nasdaq 100 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'Nasdaq 100', pros: ['3x Nasdaq short from EU', 'Tech crash hedge', 'ETP structure'], cons: ['USD/EUR currency risk on top', 'Extreme decay', 'Intraday only'] },
  { ticker: '3ITS', name: 'WisdomTree FTSE MIB 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'FTSE MIB (Italy)', pros: ['3x Italy short', 'Sovereign debt crisis play', 'EUR denominated'], cons: ['Country-specific', 'Extreme decay', 'Low liquidity'] },
  { ticker: '3FRS', name: 'WisdomTree CAC 40 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'CAC 40', pros: ['3x France short', 'EU political risk hedge', 'EUR denominated'], cons: ['Country-specific', 'Extreme volatility', 'Very short-term only'] },
  { ticker: '3CHS', name: 'WisdomTree SMI 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'SMI (Swiss Market Index)', pros: ['3x Swiss short', 'CHF denominated', 'Pharma/banking crash hedge'], cons: ['Only 20 stocks', 'Extreme decay', 'Very low AUM'] },
  { ticker: 'S500', name: 'Amundi S&P 500 Daily -2x Inverse', leverage: '-2x', region: 'Europe', category: 'Leveraged', underlying: 'S&P 500', pros: ['2x S&P short, Amundi brand', 'UCITS compliant', 'EUR hedged option available'], cons: ['Currency risk', 'Leverage decay', 'Short-term only'] },
  { ticker: '3EMS', name: 'WisdomTree EM 3x Daily Short', leverage: '-3x', region: 'Europe', category: 'Leveraged', underlying: 'MSCI Emerging Markets', pros: ['3x EM short from EU', 'Geopolitical hedge', 'Covers China, India, Brazil'], cons: ['Extreme decay', 'Broad basket dilutes returns', 'Very short-term'] },
];

const ALL_ETFS = [...US_ETFS, ...EUROPE_ETFS];

interface LLMOutlook {
  modelId: string;
  label: string;
  status: 'pending' | 'fetching' | 'success' | 'error';
  content?: string;
  error?: string;
}

const OUTLOOK_MODELS = [
  { id: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
  { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { id: 'openai/gpt-5-mini', label: 'GPT-5 Mini' },
  { id: 'openai/gpt-5', label: 'GPT-5' },
  { id: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (Finance)' },
];

interface ShortETFPanelProps {
  restoreId?: string | null;
  onHelpClick?: () => void;
}

const ShortETFPanel = ({ restoreId, onHelpClick }: ShortETFPanelProps) => {
  const { toast } = useToast();
  const [outlooks, setOutlooks] = useState<LLMOutlook[]>(
    OUTLOOK_MODELS.map(m => ({ modelId: m.id, label: m.label, status: 'pending' }))
  );
  const [loadingOutlook, setLoadingOutlook] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOutlooks, setSavedOutlooks] = useState<any[]>([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [restoredDate, setRestoredDate] = useState<string | null>(null);

  const restoreOutlook = useCallback((saved: any) => {
    const data = saved.outlooks_data as any[];
    const restored: LLMOutlook[] = OUTLOOK_MODELS.map(m => {
      const found = data.find((d: any) => d.modelId === m.id);
      return found
        ? { modelId: m.id, label: m.label, status: 'success' as const, content: found.content }
        : { modelId: m.id, label: m.label, status: 'pending' as const };
    });
    setOutlooks(restored);
    setRestoredDate(saved.created_at);
  }, []);

  const loadSavedOutlooks = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const { data, error } = await supabase
        .from('market_outlooks')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      setSavedOutlooks(data || []);
    } catch (e) {
      console.error('Failed to load saved outlooks:', e);
    } finally {
      setLoadingSaved(false);
    }
  }, []);

  useEffect(() => {
    loadSavedOutlooks();
  }, [loadSavedOutlooks]);

  // Auto-restore from restoreId prop (e.g. from Collected Data)
  useEffect(() => {
    if (!restoreId) return;
    const autoRestore = async () => {
      try {
        const { data, error } = await supabase
          .from('market_outlooks')
          .select('*')
          .eq('id', restoreId)
          .single();
        if (error) throw error;
        if (data) {
          restoreOutlook(data);
          toast({ title: 'Outlook Restored', description: `Loaded analysis from ${format(new Date(data.created_at), 'dd MMM yyyy HH:mm')}` });
        }
      } catch (e) {
        console.error('Failed to auto-restore outlook:', e);
      }
    };
    autoRestore();
  }, [restoreId, restoreOutlook, toast]);

  const saveOutlooks = async () => {
    const successOutlooks = outlooks.filter(o => o.status === 'success');
    if (!successOutlooks.length) return;

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({ title: 'Not authenticated', description: 'Please log in to save outlooks.', variant: 'destructive' });
        return;
      }
      const { error } = await supabase.from('market_outlooks').insert({
        user_id: session.user.id,
        outlooks_data: successOutlooks.map(o => ({ modelId: o.modelId, label: o.label, content: o.content })) as any,
        models_count: successOutlooks.length,
      });
      if (error) throw error;
      toast({ title: 'Outlook Saved', description: `${successOutlooks.length} model analyses saved.` });
      loadSavedOutlooks();
    } catch (e: any) {
      toast({ title: 'Save Failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreOutlook = (saved: any) => {
    restoreOutlook(saved);
    toast({ title: 'Outlook Restored', description: `Loaded analysis from ${format(new Date(saved.created_at), 'dd MMM yyyy HH:mm')}` });
  };

  const deleteOutlook = async (id: string) => {
    try {
      const { error } = await supabase.from('market_outlooks').delete().eq('id', id);
      if (error) throw error;
      setSavedOutlooks(prev => prev.filter(s => s.id !== id));
      toast({ title: 'Outlook Deleted' });
    } catch (e: any) {
      toast({ title: 'Delete Failed', description: e.message, variant: 'destructive' });
    }
  };

  const exportOutlookPDF = () => {
    const successOutlooks = outlooks.filter(o => o.status === 'success' && o.content);
    if (!successOutlooks.length) return;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    let y = margin;

    const addPageIfNeeded = (requiredSpace: number) => {
      if (y + requiredSpace > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 64, 175);
    doc.text('Multi-LLM Market Outlook Report', margin, y);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')} • ${successOutlooks.length} models`, margin, y);
    y += 4;

    // Divider
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    successOutlooks.forEach((outlook, idx) => {
      addPageIfNeeded(20);

      // Model header
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(16, 185, 129);
      doc.text(`${idx + 1}. ${outlook.label}`, margin, y);
      y += 7;

      // Content
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);

      const cleanContent = (outlook.content || '')
        .replace(/#{1,6}\s*/g, '')
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/`/g, '');

      const lines = doc.splitTextToSize(cleanContent, maxWidth);
      for (const line of lines) {
        addPageIfNeeded(5);
        doc.text(line, margin, y);
        y += 4.5;
      }

      y += 6;

      // Separator between models
      if (idx < successOutlooks.length - 1) {
        addPageIfNeeded(8);
        doc.setDrawColor(220, 220, 220);
        doc.setLineDashPattern([2, 2], 0);
        doc.line(margin, y, pageWidth - margin, y);
        doc.setLineDashPattern([], 0);
        y += 8;
      }
    });

    // Footer on each page
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`Market Outlook Report • Page ${i}/${totalPages}`, margin, pageHeight - 8);
    }

    doc.save(`market-outlook-${format(new Date(), 'yyyy-MM-dd-HHmm')}.pdf`);
    toast({ title: 'PDF Exported', description: 'Market outlook report downloaded.' });
  };

  const getLeverageBadgeColor = (leverage: string) => {
    if (leverage === '-1x') return 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30';
    if (leverage === '-2x') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
    return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30';
  };

  const renderETFTable = (etfs: ETFEntry[]) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">Ticker</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-[70px] text-center">Leverage</TableHead>
            <TableHead>Underlying</TableHead>
            <TableHead>Pros</TableHead>
            <TableHead>Cons</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {etfs.map((etf, idx) => (
            <TableRow key={`${etf.ticker}-${idx}`} className="hover:bg-muted/50">
              <TableCell className="font-mono font-bold text-primary">{etf.ticker}</TableCell>
              <TableCell className="text-sm font-medium">{etf.name}</TableCell>
              <TableCell className="text-center">
                <Badge variant="outline" className={`text-xs ${getLeverageBadgeColor(etf.leverage)}`}>
                  {etf.leverage}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">{etf.underlying}</TableCell>
              <TableCell>
                <ul className="space-y-0.5">
                  {etf.pros.map((p, i) => (
                    <li key={i} className="flex items-start gap-1 text-xs">
                      <CheckCircle2 className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{p}</span>
                    </li>
                  ))}
                </ul>
              </TableCell>
              <TableCell>
                <ul className="space-y-0.5">
                  {etf.cons.map((c, i) => (
                    <li key={i} className="flex items-start gap-1 text-xs">
                      <XCircle className="h-3 w-3 text-red-400 mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{c}</span>
                    </li>
                  ))}
                </ul>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const fetchOutlooks = async () => {
    setLoadingOutlook(true);
    setRestoredDate(null);
    setOutlooks(OUTLOOK_MODELS.map(m => ({ modelId: m.id, label: m.label, status: 'fetching' })));

    const systemPrompt = `You are a senior macro-economic strategist. Provide a concise but insightful analysis of the current state of US and European stock markets. Cover:
1. Current market trend and momentum (bullish/bearish/neutral)
2. Key risks and catalysts ahead
3. Sector rotation opportunities
4. Recommendation: Should investors hedge now? Which short ETFs are most appropriate?
5. Time horizon for your outlook (short/medium/long term)
Be specific with data points, recent events, and actionable recommendations. Use markdown formatting.`;

    const userPrompt = `Provide your current analysis and recommendation on:
- US stock market outlook (S&P 500, Nasdaq, Russell 2000)  
- European stock market outlook (Euro Stoxx 50, DAX, FTSE 100, CAC 40)
- Whether investors should consider short/inverse ETFs right now
- Which specific short ETFs from this list would be most appropriate and why:
  US: SH, PSQ, DOG, SDS, SPXU, QID, SQQQ, SDOW, TZA, SOXS
  Europe: XSPS, XSSX, BX4, 3USS, 3STS

Today's date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.`;

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    };

    const promises = OUTLOOK_MODELS.map(async (m) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            messages: [{ role: 'user', content: userPrompt }],
            model: m.id,
            systemPrompt,
            stream: false,
          }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errText = await response.text();
          return { modelId: m.id, label: m.label, status: 'error' as const, error: `HTTP ${response.status}: ${errText}` };
        }

        const data = await response.json();
        const content = data.choices?.[0]?.message?.content || data.error || 'No response';
        if (data.error) {
          return { modelId: m.id, label: m.label, status: 'error' as const, error: data.error };
        }
        return { modelId: m.id, label: m.label, status: 'success' as const, content };
      } catch (e: any) {
        return { modelId: m.id, label: m.label, status: 'error' as const, error: e.message || 'Request failed' };
      }
    });

    const results = await Promise.all(promises);
    setOutlooks(results);
    setLoadingOutlook(false);

    const successCount = results.filter(r => r.status === 'success').length;
    toast({
      title: successCount > 0 ? 'Market Outlook Ready' : 'Outlook Failed',
      description: `${successCount}/${OUTLOOK_MODELS.length} models returned analysis.`,
      variant: successCount > 0 ? 'default' : 'destructive',
    });
  };

  const hasSuccessfulOutlooks = outlooks.some(o => o.status === 'success');

  return (
    <div className="mt-10 space-y-6">
      <Card className="border-primary/20 shadow-lg">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-500/10 rounded-xl">
              <TrendingDown className="h-7 w-7 text-red-500" />
            </div>
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                Short & Inverse ETF Reference
                {onHelpClick && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button onClick={(e) => { e.stopPropagation(); onHelpClick(); }} className="text-muted-foreground hover:text-primary transition-colors">
                        <HelpCircle className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Open help for Short ETFs</TooltipContent>
                  </Tooltip>
                )}
              </CardTitle>
              <CardDescription>
                Non-leveraged and leveraged inverse ETF possibilities for US and European markets
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="us" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="us" className="gap-1.5">
                <DollarSign className="h-4 w-4" />
                US Markets
              </TabsTrigger>
              <TabsTrigger value="europe" className="gap-1.5">
                <Globe className="h-4 w-4" />
                European Markets
              </TabsTrigger>
            </TabsList>

            <TabsContent value="us">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30">-1x</Badge>
                    Non-Leveraged Inverse ETFs
                  </h3>
                  {renderETFTable(US_ETFS.filter(e => e.category === 'Non-Leveraged'))}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Badge variant="outline" className="bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30">-2x / -3x</Badge>
                    Leveraged Inverse ETFs
                  </h3>
                  <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 mb-3">
                    <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span><strong>Warning:</strong> Leveraged inverse ETFs are designed for daily trading only. Holding them for more than one day can result in significant tracking errors and value erosion due to daily compounding. They are NOT suitable for long-term hedging.</span>
                    </p>
                  </div>
                  {renderETFTable(US_ETFS.filter(e => e.category === 'Leveraged'))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="europe">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30">-1x</Badge>
                    Non-Leveraged Inverse ETFs (UCITS)
                  </h3>
                  {renderETFTable(EUROPE_ETFS.filter(e => e.category === 'Non-Leveraged'))}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Badge variant="outline" className="bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30">-2x / -3x</Badge>
                    Leveraged Inverse ETFs (UCITS / ETP)
                  </h3>
                  <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 mb-3">
                    <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span><strong>Warning:</strong> Leveraged products compound daily. In volatile or sideways markets, value can erode rapidly. Use only for very short-term tactical trades.</span>
                    </p>
                  </div>
                  {renderETFTable(EUROPE_ETFS.filter(e => e.category === 'Leveraged'))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Multi-LLM Market Outlook */}
      <Card className="border-primary/20 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-500/10 rounded-xl">
                <Brain className="h-7 w-7 text-purple-500" />
              </div>
              <div>
                <CardTitle className="text-2xl flex items-center gap-2">
                  Multi-LLM Market Outlook
                  {restoredDate && (
                    <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30">
                      <Clock className="h-3 w-3 mr-1" />
                      {format(new Date(restoredDate), 'dd MMM yyyy, HH:mm')}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  4 AI models analyse current US & European market conditions and short ETF recommendations
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasSuccessfulOutlooks && (
                <>
                  <Button variant="outline" size="sm" onClick={exportOutlookPDF} className="gap-1">
                    <FileDown className="h-3 w-3" />
                    Export PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={saveOutlooks} disabled={saving} className="gap-1">
                    <Save className="h-3 w-3" />
                    {saving ? 'Saving...' : 'Save Outlook'}
                  </Button>
                </>
              )}
              <Button onClick={fetchOutlooks} disabled={loadingOutlook} className="gap-2">
                {loadingOutlook ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
                {loadingOutlook ? 'Analysing...' : 'Get AI Outlook'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Saved Outlooks */}
          {!loadingSaved && savedOutlooks.length > 0 && outlooks.every(o => o.status === 'pending') && (
            <Card className="border-primary/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <History className="h-4 w-4 text-primary" />
                  Saved Market Outlooks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {savedOutlooks.map(saved => (
                    <div key={saved.id} className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-muted/30 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">
                            {format(new Date(saved.created_at), 'dd MMM yyyy — HH:mm')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {saved.models_count} AI models
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" variant="default" onClick={() => handleRestoreOutlook(saved)} className="gap-1">
                          <History className="h-3 w-3" />
                          Restore
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteOutlook(saved.id)} className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {outlooks.every(o => o.status === 'pending') && savedOutlooks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">Click "Get AI Outlook" to query 4 AI models</p>
              <p className="text-sm mt-1">Each model will provide independent analysis of current US & European market conditions</p>
            </div>
          ) : !outlooks.every(o => o.status === 'pending') ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {outlooks.map((outlook) => (
                <Card key={outlook.modelId} className={`border ${
                  outlook.status === 'fetching' ? 'border-primary/40 animate-pulse' :
                  outlook.status === 'success' ? 'border-emerald-500/30' :
                  outlook.status === 'error' ? 'border-destructive/30' : 'border-border'
                }`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold">{outlook.label}</CardTitle>
                      <Badge variant="outline" className={`text-xs ${
                        outlook.status === 'fetching' ? 'bg-primary/10 text-primary border-primary/30' :
                        outlook.status === 'success' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' :
                        outlook.status === 'error' ? 'bg-destructive/10 text-destructive border-destructive/30' :
                        ''
                      }`}>
                        {outlook.status === 'fetching' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                        {outlook.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {outlook.status === 'fetching' && (
                      <div className="space-y-2">
                        <div className="h-4 bg-muted rounded animate-pulse w-full" />
                        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                        <div className="h-4 bg-muted rounded animate-pulse w-5/6" />
                      </div>
                    )}
                    {outlook.status === 'success' && outlook.content && (
                      <div className="prose prose-sm dark:prose-invert max-w-none max-h-[500px] overflow-y-auto text-sm">
                        <ReactMarkdown>{outlook.content}</ReactMarkdown>
                      </div>
                    )}
                    {outlook.status === 'error' && (
                      <p className="text-sm text-destructive">{outlook.error}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShortETFPanel;
